<template>
  <DsPageContent>
    <!-- Hero with Media -->
    <DsHeroWithMedia
      v-if="content.hero"
      :screen="screen"
      :title="content.hero.title"
      :image="content.hero.image"
      :links="content.hero.links"
      :eyebrow="content.hero.eyebrow"
      :icon="content.hero.icon"
      aspect-ratio="3/4"
    >
      <template
        v-if="content.hero.description"
        #description
      >
        <RichText
          :data="content.hero.description"
          class="ds-text-medium"
        />
      </template>
    </DsHeroWithMedia>

    <!-- Logobar -->
    <DsSection v-if="content.logoBar">
      <DsLogoBar
        v-if="content.logoBar"
        :items="content.logoBar.items"
        :title="content.logoBar.title"
        align-center
      />
    </DsSection>

    <!-- Benefits - array of USPs -->
    <DsSection v-if="content.benefitsCollection">
      <DsGrid
        :column-width="{ small: 12, medium: 4, large: 4 }"
        :items="content.benefitsCollection"
      >
        <template #default="{ item }">
          <DsCardTextIcon
            v-if="item"
            :icon="item.icon"
            :title="item.title"
          >
            <RichText
              v-if="item.description"
              :data="item.description"
            />
          </DsCardTextIcon>
        </template>
      </DsGrid>
    </DsSection>

    <!-- Intro media -->
    <DsSection v-if="content.image">
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsImageElement :image="content.image" />
        </div>
      </div>
    </DsSection>

    <!-- Content Block 1 -->
    <DsSectionGroup
      v-if="content.contentBlock1 && content.contentBlock1.items && content.contentBlock1.items.length"
      :title="content.contentBlock1.title"
      :subtitle="content.contentBlock1.description"
      :with-space-top="false"
    >
      <DsGrid
        class="ds-margin-top-24"
        :column-width="{ small: 12, medium: 4, large: 4 }"
        :items="content.contentBlock1.items"
      >
        <template #default="{ item }">
          <DsCardTextIcon
            v-if="item"
            :icon="item.icon"
            :title="item.title"
            :link="item.links[0]"
            with-title-size-small
          >
            <RichText
              v-if="item.description"
              :data="item.description"
            />
          </DsCardTextIcon>
        </template>
      </DsGrid>
    </DsSectionGroup>

    <!-- Content Block 2 -->
    <DsSection
      v-if="content.contentBlock2"
      theme="grey"
    >
      <DsMediaTextBlock
        :title="content.contentBlock2.title"
        :image="content.contentBlock2.image"
        :link="content.contentBlock2.links[0]"
        :screen="screen"
        orientation="bottom"
        center-aligned
      >
        <RichText :data="content.contentBlock2.description" />
      </DsMediaTextBlock>
    </DsSection>

    <!-- Content Block 3 -->
    <DsSectionGroup
      v-if="content.contentBlock3 && content.contentBlock3.items && content.contentBlock3.items.length"
      :title="content.contentBlock3.title"
      :subtitle="content.contentBlock3.description"
    >
      <DsGrid
        :column-width="{ small: 12, medium: 4, large: 3 }"
        :items="content.contentBlock3.items"
      >
        <template #default="{ item }">
          <DsCardMedia
            v-if="item"
            :icon="item.icon"
            :title="item.title"
            :link="item.links[0]"
            with-title-size-small
          >
            <RichText
              v-if="item.description"
              :data="item.description"
            />
          </DsCardMedia>
        </template>
      </DsGrid>
    </DsSectionGroup>

    <!-- NEW: Image -->
    <DsSectionImage
      v-if="content.image2"
      :image="content.image2"
      theme-variant="light-grey"
      :screen="screen"
      is-contained
    />

    <!-- NEW: Content Block 4 - Block - USP -->
    <DsSection
      v-if="content.contentBlockUsp"
      :with-space-bottom="false"
      theme="grey"
    >
      <BlockUspList
        :items="content.contentBlockUsp.items"
        :title="content.contentBlockUsp.title"
        :description="content.contentBlockUsp.subtitle"
      />
    </DsSection>

    <DsSection
      :with-space-bottom="false"
      theme="grey"
      aria-hidden="true"
    >
      <div class="ds-grid">
        <div class="ds-grid__col ds-grid__col--12">
          <DsDivider theme="light" />
        </div>
      </div>
    </DsSection>

    <!-- NEW: Content Block 5 -> old Content Block 4 (rename): Image Gallery  -->
    <DsSectionGroup
      v-if="content.contentBlock4 && content.contentBlock4.items && content.contentBlock4.items.length"
      theme="grey"
      :title="content.contentBlock4.title"
      :subtitle="content.contentBlock4.description"
      :with-space-bottom="false"
      full-screen
      class="ds-padding-bottom-96"
    >
      <ImageGalleryGroup
        :items="content.contentBlock4.items"
        :items-width="galleryItemsWidth"
        :tabs-label="content.contentBlock4.title"
        :screen="screen"
      >
        <template #item="{ item }">
          <div v-if="item">
            <p class="ds-margin-bottom-12">
              {{ item.title }}
            </p>
            <DsCardMediaWithImage :image="item.image" />
          </div>
        </template>
      </ImageGalleryGroup>
    </DsSectionGroup>

    <!-- NEW: Content Block 6 -> old Content Block 7 (rename): Media Text  -->
    <DsSection v-if="content.contentBlock7">
      <DsMediaTextBlock
        :title="content.contentBlock7.title"
        :image="content.contentBlock7.image"
        :link="content.contentBlock7.links[0]"
        :screen="screen"
      >
        <RichText :data="content.contentBlock7.description" />
      </DsMediaTextBlock>
    </DsSection>

    <!-- NEW: CTA -> old Content Block 8 used as CTA (refactor) -->
    <DsCallToAction
      v-if="content.contentBlock8"
      :title="content.contentBlock8.title"
      :text="content.contentBlock8.text"
      :primary-link="content.contentBlock8.primaryLink"
    />

    <!-- NEW: FAQ Block -->
    <DsSection v-if="content.faq">
      <FaqGroup v-bind="content.faq" />
    </DsSection>

    <!-- TODO: Add here the other block with articles retrieved automatically from the knowledge hub -->
  </DsPageContent>
</template>
<script setup>
import {
  DsPageContent,
  DsSection,
  DsSectionGroup,
  DsGrid,
  DsHeroWithMedia,
  DsLogoBar,
  DsImageElement,
  DsSectionImage,
  DsMediaTextBlock,
  DsCardTextIcon,
  DsCardMedia,
  DsCardMediaWithImage,
  DsCallToAction,
  DsDivider
} from 'awds'
import ProductDefinitions from '../../../core/data-layer/definitions/products'
import mapperImage from '../../../core/data-layer/utils/mappers/mapper-image'

const { locale } = useI18n()
const { useStandardAsyncData, onAfterAdyenData, onBeforeAdyenData, content } = useMainPageData()
const { screen } = useScreen()

const { PageProductOnlinePayments } = ProductDefinitions

await onBeforeAdyenData({ globalDataContentType: PageProductOnlinePayments.contentType })

const { data } = await useAsyncData('products-online-payments' + locale.value, async () => {
  const page = await useStandardAsyncData(PageProductOnlinePayments)

  // @TODO Nico: refactor logic around image sizes
  // Update gallery images to use correct image sizes (because of BlockTabContentLazy)
  const galleryImageSizes = {
    small: { width: 336, height: 'auto' },
    medium: { width: 320, height: 'auto' },
    large: { width: 368, height: 'auto' }
  }
  const galleryItemsWidth = { small: 336, medium: 320, large: 368 }
  const contentGallery = page.pageData?.content?.contentBlock4.items.map((tabItem) => {
    return {
      ...tabItem,
      items: tabItem.items.map((card) => {
        return { ...card, image: mapperImage(card.image.original, galleryImageSizes) }
      })
    }
  })
  // replace mapper gallery items
  // Warning! array mutation
  page.pageData.content.contentBlock4.items = contentGallery

  return { pageData: page.pageData, galleryItemsWidth }
})

const galleryItemsWidth = computed(() => {
  return data.value?.galleryItemsWidth || {}
})

await onAfterAdyenData(data.value)
</script>
